/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DiagnosticLogger } from "./DiagnosticLogger";
import { OperationalLogger } from "./OperationalLogger";
import type { LogDetails } from "../types";
import { DiagnosticSeverity } from "../types";

/** Caller-supplied post-sign-in telemetry fields. */
export type PostSigninLogFields = Record<string, string | undefined>;

const EVENT_API: string = "post_signin_api";
const EVENT_FLOW: string = "post_signin_flow";

const EVENT_API_REQUEST: string = "apiRequest";
const EVENT_STARTUP_START: string = "postSignInStartupStart";
const EVENT_STARTUP_END: string = "postSignInStartupEnd";
const EVENT_STARTUP_BLOCKED: string = "postSignInStartupBlocked";

const STATUS_SUCCESS: string = "success";
const STATUS_FAILURE: string = "failure";
const UNKNOWN_ERROR_TYPE: string = "unknown";

const KEY_API: string = "api";
const KEY_API_NAME: string = "api_name";
const KEY_EVENT: string = "event";
const KEY_ERROR_TYPE: string = "error_type";
const KEY_HTTP_STATUS: string = "http_status";
const KEY_LATENCY_MS: string = "latency_ms";
const KEY_STATUS: string = "status";

/** Post-sign-in APIs owned by this startup telemetry phase. */
export const PostSigninApi = {
  DeviceFeatures: "deviceFeatures",
  BrandingBundle: "brandingBundle",
  StringOverrides: "stringOverrides",
  QuickMenuShortcuts: "quickMenuShortcuts",
  NpvrAllowRecording: "npvrAllowRecording",
  WanIpAddress: "wanIpAddress",
  InHomeLocation: "inHomeLocation",
  VideoProviderConfiguration: "videoProviderConfiguration",
  ScopedDomainToken: "scopedDomainToken",
  InternalRatings: "internalRatings",
  SoftClientCert: "softClientCert",
  WebSocketConnection: "webSocketConnection"
} as const;

export type PostSigninApiName =
  (typeof PostSigninApi)[keyof typeof PostSigninApi];

/** Logical units in the post-sign-in startup sequence. */
export const PostSigninStartupStep = {
  SageEndpoints: "sageEndpoints",
  DeviceFeatures: "deviceFeatures",
  BrandingBundle: "brandingBundle",
  StringOverrides: "stringOverrides",
  QuickMenuShortcuts: "quickMenuShortcuts",
  NpvrAllowRecording: "npvrAllowRecording",
  WanIpAddress: "wanIpAddress",
  InHomeLocation: "inHomeLocation",
  VideoProviderConfiguration: "videoProviderConfiguration",
  InternalRatings: "internalRatings",
  WebSocketConnection: "webSocketConnection"
} as const;

export type PostSigninStartupStepName =
  (typeof PostSigninStartupStep)[keyof typeof PostSigninStartupStep];

/** `post_signin_flow` discriminators. */
export const PostSigninFlowEvent = {
  StartupStarted: "startup_started",
  StepStarted: "step_started",
  StepCompleted: "step_completed",
  StepFailed: "step_failed",
  StepSkipped: "step_skipped",
  ProfileSelectionRequired: "profile_selection_required",
  StartupCompleted: "startup_completed",
  StartupBlocked: "startup_blocked"
} as const;

export type PostSigninFlowEventName =
  (typeof PostSigninFlowEvent)[keyof typeof PostSigninFlowEvent];

/** Terminal outcomes for one post-sign-in startup run. */
export const PostSigninStartupResult = {
  Success: "success",
  Degraded: "degraded",
  AwaitingProfile: "awaiting_profile",
  Failure: "failure"
} as const;

export type PostSigninStartupResultName =
  (typeof PostSigninStartupResult)[keyof typeof PostSigninStartupResult];

/**
 * Structured telemetry facade for the post-sign-in startup phase.
 *
 * API events are emitted at physical request boundaries. Startup lifecycle
 * events are emitted by the post-sign-in orchestration. All details must be
 * pre-redacted: never provide tokens, identifiers, profile data, or raw error
 * messages.
 */
export class PostSigninLogger {
  /** Emits diagnostic and operational telemetry for one successful API call. */
  public static logApiSuccess(
    api: PostSigninApiName,
    latencyMs: number,
    extra?: PostSigninLogFields
  ): void {
    const details: LogDetails = {
      [KEY_API]: api,
      [KEY_STATUS]: STATUS_SUCCESS,
      [KEY_LATENCY_MS]: String(latencyMs)
    };
    PostSigninLogger.assign(details, extra);

    DiagnosticLogger.log(
      EVENT_API,
      api + " succeeded",
      DiagnosticSeverity.Info,
      details
    );
    PostSigninLogger.logApiRequest(api, STATUS_SUCCESS, latencyMs, extra);
  }

  /** Emits diagnostic and operational telemetry for one failed API call. */
  public static logApiFailure(
    api: PostSigninApiName,
    error: unknown,
    severity: DiagnosticSeverity,
    latencyMs: number,
    extra?: PostSigninLogFields
  ): void {
    const details: LogDetails = {
      [KEY_API]: api,
      [KEY_STATUS]: STATUS_FAILURE
    };
    const errorDetails: LogDetails = PostSigninLogger.buildErrorDetails(error);
    PostSigninLogger.assign(details, errorDetails);
    PostSigninLogger.assign(details, extra);

    DiagnosticLogger.log(EVENT_API, api + " failed", severity, details);
    PostSigninLogger.logApiRequest(api, STATUS_FAILURE, latencyMs, {
      ...extra,
      ...errorDetails
    });
  }

  /** Emits a post-sign-in startup state transition or decision. */
  public static logFlowEvent(
    event: PostSigninFlowEventName,
    message: string,
    severity: DiagnosticSeverity,
    extra?: PostSigninLogFields
  ): void {
    const details: LogDetails = { [KEY_EVENT]: event };
    PostSigninLogger.assign(details, extra);
    DiagnosticLogger.log(EVENT_FLOW, message, severity, details);
  }

  /** Emits a redacted failed post-sign-in startup state transition. */
  public static logFlowFailure(
    event: PostSigninFlowEventName,
    message: string,
    error: unknown,
    extra?: PostSigninLogFields
  ): void {
    const details: LogDetails = PostSigninLogger.buildErrorDetails(error);
    PostSigninLogger.assign(details, extra);
    PostSigninLogger.logFlowEvent(
      event,
      message,
      DiagnosticSeverity.Error,
      details
    );
  }

  /** Opens the operational transaction for one startup run. */
  public static logStartupStart(fields?: PostSigninLogFields): string {
    return OperationalLogger.transactionStart(
      EVENT_STARTUP_START,
      PostSigninLogger.toDetails(fields)
    );
  }

  /** Closes the operational transaction for one startup run. */
  public static logStartupEnd(
    transactionId: string,
    fields: PostSigninLogFields
  ): void {
    OperationalLogger.transactionEnd(
      EVENT_STARTUP_END,
      transactionId,
      PostSigninLogger.toDetails(fields)
    );
  }

  /** Reports that critical startup retry has become visibly blocking. */
  public static logStartupBlocked(fields: PostSigninLogFields): void {
    OperationalLogger.logEvent(
      EVENT_STARTUP_BLOCKED,
      PostSigninLogger.toDetails(fields)
    );
  }

  /** Redacted error classification for telemetry. */
  public static describeErrorType(error: unknown): string {
    if (error instanceof Error) {
      return error.constructor.name;
    }
    return UNKNOWN_ERROR_TYPE;
  }

  /** Returns an Axios-style response status when one is available. */
  public static extractHttpStatus(error: unknown): string | undefined {
    if (error === null || error === undefined || typeof error !== "object") {
      return undefined;
    }
    const errorRecord = error as Record<string, unknown>;
    const response = errorRecord.response as
      | Record<string, unknown>
      | undefined;
    if (response === null || response === undefined) {
      return undefined;
    }
    const status: number = Number(response.status);
    return Number.isNaN(status) ? undefined : String(status);
  }

  private static logApiRequest(
    apiName: PostSigninApiName,
    status: string,
    latencyMs: number,
    extra?: PostSigninLogFields
  ): void {
    const details: LogDetails = {
      [KEY_API_NAME]: apiName,
      [KEY_STATUS]: status,
      [KEY_LATENCY_MS]: String(latencyMs)
    };
    PostSigninLogger.assign(details, extra);
    OperationalLogger.logEvent(EVENT_API_REQUEST, details);
  }

  private static toDetails(fields?: PostSigninLogFields): LogDetails {
    const details: LogDetails = {};
    PostSigninLogger.assign(details, fields);
    return details;
  }

  private static buildErrorDetails(error: unknown): LogDetails {
    const details: LogDetails = {
      [KEY_ERROR_TYPE]: PostSigninLogger.describeErrorType(error)
    };
    const httpStatus: string | undefined =
      PostSigninLogger.extractHttpStatus(error);
    if (httpStatus !== undefined) {
      details[KEY_HTTP_STATUS] = httpStatus;
    }
    return details;
  }

  private static assign(target: LogDetails, extra?: PostSigninLogFields): void {
    if (extra === undefined || extra === null) {
      return;
    }
    const keys: string[] = Object.keys(extra);
    for (let index = 0; index < keys.length; index += 1) {
      const value: string | undefined = extra[keys[index]];
      if (value !== undefined) {
        target[keys[index]] = value;
      }
    }
  }
}
